import axios, { AxiosInstance } from 'axios';

// Key names in localStorage
const API_OVERRIDE_KEY = 'apiBaseUrlOverride';
const API_ENV_KEY = 'apiEnvironment'; // 'dev' | 'prod'
const DEFAULT_DEV_URL = 'http://localhost:8000';
const PROD_URL = 'https://api.bbe.studio27.se';

// Determine if current user is a developer
function isDeveloper(): boolean {
  try {
    const meRaw = window.localStorage.getItem('me');
    if (!meRaw) return false;
    const me = JSON.parse(meRaw);
    return !!me?.developer;
  } catch {
    return false;
  }
}

export type ApiEnvironment = 'dev' | 'prod';

export function getApiEnvironment(): ApiEnvironment {
  if (!isDeveloper()) return 'prod';
  const env = window.localStorage.getItem(API_ENV_KEY);
  return env === 'prod' ? 'prod' : 'dev'; // default dev for developers
}

export function setApiEnvironment(env: ApiEnvironment) {
  if (!isDeveloper()) return; // ignore for non-devs
  window.localStorage.setItem(API_ENV_KEY, env);
  window.dispatchEvent(new Event('apiBaseUrlChanged'));
  clearApiCache();
}

export function getApiBaseUrl(): string {
  const env = getApiEnvironment();
  if (env === 'dev') {
    const override = window.localStorage.getItem(API_OVERRIDE_KEY);
    if (override && override.trim() !== '') {
      return override.trim().replace(/\/$/, '');
    }
    return DEFAULT_DEV_URL;
  }
  return PROD_URL;
}

export function setApiBaseUrlOverride(url: string | null) {
  if (!isDeveloper()) return; // only for devs
  if (url && url.trim() !== '') {
    window.localStorage.setItem(API_OVERRIDE_KEY, url.trim());
  } else {
    window.localStorage.removeItem(API_OVERRIDE_KEY);
  }
  // ensure environment stays dev if override set
  if (url && url.trim() !== '') {
    setApiEnvironment('dev');
  }
  window.dispatchEvent(new Event('apiBaseUrlChanged'));
  clearApiCache();
}

export function isApiOverridden(): boolean {
  if (getApiEnvironment() !== 'dev') return false;
  try {
    const override = window.localStorage.getItem(API_OVERRIDE_KEY);
    return !!override && override.trim() !== '' && override.trim().replace(/\/$/, '') !== DEFAULT_DEV_URL;
  } catch {
    return false;
  }
}

let apiInstance: AxiosInstance | null = null;
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

export function getApi(): AxiosInstance {
  const baseURL = getApiBaseUrl();
  if (!apiInstance || apiInstance.defaults.baseURL !== baseURL) {
    apiInstance = axios.create({
      baseURL,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
        // Authorization injected dynamically in request interceptor
      }
    });

    // Request logging
    apiInstance.interceptors.request.use(cfg => {
      // Always pull latest token so a fresh login is respected
      const token = window.localStorage.getItem('token');
      if (token) {
        cfg.headers = cfg.headers || {};
        // JWT tokens use 'Bearer' authentication
        cfg.headers['Authorization'] = 'Bearer ' + token;
      } else if (cfg.headers && 'Authorization' in cfg.headers) {
        delete (cfg.headers as any)['Authorization'];
      }
      try {
        const fullUrl = `${cfg.baseURL || ''}${cfg.url || ''}`;
        console.debug('[API REQUEST]', (cfg.method || 'GET').toUpperCase(), fullUrl, cfg.params || '', cfg.data || '');
      } catch { /* ignore */ }
      return cfg;
    });

    // Response / error handling
    apiInstance.interceptors.response.use(
      resp => {
        try {
          console.debug('[API RESPONSE]', resp.status, resp.config.url, resp.data && typeof resp.data === 'object' ? '' : resp.data);
        } catch { /* ignore */ }
        return resp;
      },
      async error => {
        const originalRequest = error.config;
        const status = error?.response?.status;
        
        // If 401 and we haven't tried to refresh yet
        if (status === 401 && !originalRequest._retry) {
          if (isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then(token => {
              originalRequest.headers['Authorization'] = 'Bearer ' + token;
              return axios(originalRequest);
            }).catch(err => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          const refreshToken = window.localStorage.getItem('refresh_token');
          
          if (!refreshToken) {
            // No refresh token available, redirect to login
            console.warn('[API 401] No refresh token available, redirecting to login.');
            window.localStorage.removeItem('token');
            window.localStorage.removeItem('refresh_token');
            if (!window.location.pathname.toLowerCase().includes('/login')) {
              window.location.href = '/login';
            }
            return Promise.reject(error);
          }

          try {
            // Try to refresh the token
            console.log('[API 401] Attempting to refresh token');
            const response = await axios.post(`${getApiBaseUrl()}/api/token/refresh/`, {
              refresh: refreshToken
            });

            if (response.data.access) {
              const newToken = response.data.access;
              window.localStorage.setItem('token', newToken);
              console.log('[API 401] Token refreshed successfully');
              
              // Update the authorization header
              originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
              
              // Process queued requests
              processQueue(null, newToken);
              isRefreshing = false;
              
              // Retry the original request
              return axios(originalRequest);
            } else {
              throw new Error('No access token in refresh response');
            }
          } catch (refreshError) {
            console.error('[API 401] Token refresh failed:', refreshError);
            processQueue(refreshError, null);
            isRefreshing = false;
            
            // Refresh failed, clear tokens and redirect to login
            window.localStorage.removeItem('token');
            window.localStorage.removeItem('refresh_token');
            if (!window.location.pathname.toLowerCase().includes('/login')) {
              window.location.href = '/login';
            }
            return Promise.reject(refreshError);
          }
        }
        
        // For other errors, just log and reject
        try {
          console.error('[API ERROR]', status, error?.response?.data || error.message);
        } catch { /* ignore */ }
        return Promise.reject(error);
      }
    );
  }
  return apiInstance;
}

export function clearApiCache() {
  apiInstance = null;
}

// Convenience: toggle environment for developers
export function toggleApiEnvironment() {
  if (!isDeveloper()) return;
  const current = getApiEnvironment();
  setApiEnvironment(current === 'dev' ? 'prod' : 'dev');
}

export default getApi;
